import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Info, Save, X } from 'lucide-react';
import '../Personell/employee-form.css';

const EditPayrollModal = ({ show, handleClose, handleSave, initialDate, title, isUpdating = false, payrollPeriod = null }) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [error, setError] = useState('');

    console.log('EditPayrollModal', initialDate, title, isUpdating, payrollPeriod);

    // Helper function to format date in local timezone for input[type="date"]
    const formatDateForInput = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        // Format the date when the modal opens
        if (initialDate) {
            const formattedDate = formatDateForInput(initialDate);
            setSelectedDate(formattedDate);
        } else {
            setSelectedDate(formatDateForInput(new Date()));
        }
        setError(''); // Clear error when modal opens
    }, [initialDate, show]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
        setError(''); // Clear error when user changes date
    };

    const handleSaveClick = async () => {
        if (!selectedDate) {
            setError('Vänligen välj ett utbetalningsdatum');
            return;
        }

        // Create date in local timezone to avoid UTC conversion issues
        const [year, month, day] = selectedDate.split('-').map(Number);
        const selectedDateObj = new Date(year, month - 1, day);

        // Always validate that the payout date is not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDateObj < today) {
            setError('Utbetalningsdatum kan inte vara i det förflutna');
            return;
        }

        // Validate that the payout date falls within the payroll period
        if (payrollPeriod) {
            const periodStart = new Date(payrollPeriod.year, payrollPeriod.month - 1, 1);
            const periodEnd = new Date(payrollPeriod.year, payrollPeriod.month, 0);
            
            if (selectedDateObj < periodStart || selectedDateObj > periodEnd) {
                const monthName = new Date(payrollPeriod.year, payrollPeriod.month - 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
                const periodStartFormatted = periodStart.toLocaleDateString('sv-SE');
                const periodEndFormatted = periodEnd.toLocaleDateString('sv-SE');
                
                setError(`Utbetalningsdatum måste vara inom löneperioden för ${monthName} (${periodStartFormatted} - ${periodEndFormatted})`);
                return;
            }
        }

        try {
            await handleSave(selectedDate);
            handleClose();
            setError('');
        } catch (err) {
            setError('Ett fel uppstod när lönekörningen skulle uppdateras');
        }
    };

    const handleModalClose = () => {
        if (!isUpdating) {
            setError('');
            handleClose();
        }
    };

    const getPayoutDateInfo = () => {
        if (!selectedDate) return '';

        const date = new Date(selectedDate);
        const dayOfWeek = date.toLocaleDateString('sv-SE', { weekday: 'long' });
        const formatted = date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        return `${dayOfWeek}, ${formatted}`;
    };

    return (
        <>

            <Modal
                className="emp-modal"
                show={show}
                onHide={handleModalClose}
                backdrop={isUpdating ? 'static' : true}
                keyboard={!isUpdating}
            >
                <Modal.Header closeButton={!isUpdating}>
                    <Modal.Title>
                        {title || 'Redigera lönekörning'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="form-info">
                        <Info size={20} className="info-icon" />
                        <div className="info-text">
                            <strong>Uppdatera utbetalningsdatum</strong><br />
                            Välj det datum då lönerna kommer att betalas ut till de anställda.
                            {payrollPeriod && (
                                <>
                                    <br />
                                    <strong>Viktigt:</strong> Utbetalningsdatum måste vara inom löneperioden för {' '}
                                    {new Date(payrollPeriod.year, payrollPeriod.month - 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}
                                    {' '}({new Date(payrollPeriod.year, payrollPeriod.month - 1, 1).toLocaleDateString('sv-SE')} - {' '}
                                    {new Date(payrollPeriod.year, payrollPeriod.month, 0).toLocaleDateString('sv-SE')}).
                                </>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="validation-summary">
                            <X size={16} />
                            {error}
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            <div className="input-group">
                                <label className="input-label">
                                    Utbetalningsdatum *
                                </label>
                                <input
                                    type="date"
                                    className="modern-input"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    min={(() => {
                                        const today = formatDateForInput(new Date());
                                        if (payrollPeriod) {
                                            const periodStart = formatDateForInput(new Date(payrollPeriod.year, payrollPeriod.month - 1, 1));
                                            // Use the later of today or period start
                                            return today > periodStart ? today : periodStart;
                                        }
                                        return today;
                                    })()}
                                    max={payrollPeriod ? 
                                        formatDateForInput(new Date(payrollPeriod.year, payrollPeriod.month, 0))
                                        : undefined
                                    }
                                    disabled={isUpdating}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-6">
                            {selectedDate && (
                                <div className="current-value-card mb-0">
                                    <div className="current-value-label">Valt datum</div>
                                    <div className="current-value-text">
                                        {getPayoutDateInfo()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        type="button" className="ll-btn ll-btn--secondary"
                        onClick={handleModalClose}
                        disabled={isUpdating}
                    >
                        <X size={16} />
                        Avbryt
                    </button>

                    <button
                        type="button" className="ll-btn ll-btn--primary"
                        onClick={handleSaveClick}
                        disabled={isUpdating || !selectedDate}
                    >
                        {isUpdating ? (
                            <>
                                <div className="loading-spinner" />
                                Uppdaterar...
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                Spara ändringar
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditPayrollModal;

